// Geometry for the oval ("traditional poker table") game layout.
//
// Pure functions, no React, no DOM — so every phone size and player count can
// be tested directly. The table layout has no scrollbar: whatever the action
// dock leaves behind is ALL the room the table gets, so these numbers are
// derived from a measured box rather than hard-coded per device.

export const MIN_TABLE_W = 180;
export const MAX_TABLE_W = 300;
export const MIN_TABLE_H = 230;

// Hard floor for the oval. MIN_TABLE_H is the size the oval *wants*; this is the
// size below which it stops being a table. Between the two, the oval gives up
// height rather than push a seat off the screen — see ovalFor.
export const ABS_MIN_TABLE_H = 168;
export const MAX_TABLE_H = 520;

// Widest sensible oval. Beyond this the table reads as a stadium rather than a
// poker table, and seats at the top/bottom drift far from the pot.
export const MAX_ASPECT = 1.62;

// A table is "dense" from 7 players up: smaller seat cards so 7-10 seats fit
// around the rail without touching.
export const DENSE_FROM = 7;

// Below this wrap width we're on a small phone (a 360pt Android, an iPhone SE)
// and seat cards give up a few points of width.
const NARROW_W = 350;

// Seats shrink no further than this; past it, legibility loses to arithmetic.
export const MIN_SEAT_W = 62;

// Below this width a seat drops to its tightest type scale ("micro"): a
// shorter card and smaller type. The D/SB/BB tag sits inside the card on
// the stack line, so it never covers the name.
export const MICRO_SEAT_W = 86;

// Floor for the centre readout. The pot is the biggest thing on the table, so
// it is the first thing that gives way when the oval gets tight — that is the
// rule that keeps a 2–4 felt chip from ever landing on the pot on a small phone.
export const MIN_SCALE = 0.72;

export function clamp(lo, value, hi) {
  return Math.max(lo, Math.min(hi, value));
}

// Seat angles in degrees, clockwise, in screen coordinates (y grows downward,
// so 90 is the bottom of the oval).
//
// The viewer is ALWAYS seated at the bottom — the convention every poker app
// uses — and everyone else keeps their real table order around them, so the
// dealer button still travels the way it does at a physical table.
export function seatAngles(playerCount, heroIndex) {
  const n = Math.max(1, playerCount);
  const hero = heroIndex >= 0 ? heroIndex : 0;
  const step = 360 / n;
  return Array.from({ length: n }, (_, i) => 90 + ((((i - hero) % n) + n) % n) * step);
}

// Where one seat card's centre sits, relative to the centre of the oval.
// x is pulled in slightly so a seat on the horizontal extreme keeps its whole
// card inside the gutter.
export function seatPosition(angleDeg, tableW, tableH) {
  const a = (angleDeg * Math.PI) / 180;
  return {
    x: Math.round(((Math.cos(a) * tableW) / 2) * 0.94),
    y: Math.round((Math.sin(a) * tableH) / 2),
  };
}

// Every seat (2–10) shows a live bet as plain ink text inside the card
// ("Bet $20") on a reserved line under the stack. Felt chips and ink
// pills are gone. usesInSeatBet stays true so the 3-line seat geometry
// (name / stack / bet+tag) applies at every player count.
export const IN_SEAT_BET_FROM = 2;
export function usesInSeatBet() {
  return true;
}

// A player's live bet hangs off the edge of their seat card that faces the pot
// — anchored to the CARD, never to a radius. Anchoring it to a radius is what
// makes a bet chip land on top of the pot readout on a small phone; anchoring
// it to the card cannot, at any size or player count.
//
// Kept as the above/below hint for a seat on the vertical. Actual chip
// coordinates come from placeBetChips: a fixed inside-rail slot on the
// pot-facing edge of the card. An arc search that walked around the seat
// drifted chips outside the oval (and clipped the top/bottom ones).
// Used on 2–4 player tables only.
export function betAnchor(angleDeg) {
  const a = (angleDeg * Math.PI) / 180;
  return Math.sin(a) < 0 ? 'below' : 'above';
}

// Conservative chip box for 2–4 felt chips. The model stays a couple of
// pixels larger than the render — a chip that fits here cannot collide
// on screen.
export const CHIP_W = 34;
export const CHIP_H = 14;
export const CHIP_GAP = 1;
export const CHIP_PEER_GAP = 8;
export const CHIP_INSET = 2;

export function chipSizeFor() {
  return { w: CHIP_W, h: CHIP_H };
}

// D/SB pill inside the seat, bottom-right on the stack line
// (see TableLayout Seat). Live table passes measured tag boxes.
const TAG_W = 28;
const TAG_H = 14;
const TAG_INSET = 3;

export function aabbOverlap(a, b, gap = 0) {
  if (!a || !b) return false;
  return Math.abs(a.x - b.x) * 2 < a.w + b.w + gap
      && Math.abs(a.y - b.y) * 2 < a.h + b.h + gap;
}

export function unitToward(fromX, fromY, toX, toY) {
  const dx = toX - fromX;
  const dy = toY - fromY;
  const len = Math.hypot(dx, dy);
  if (len < 0.001) return { x: 0, y: -1 };
  return { x: dx / len, y: dy / len };
}

// Distance from an AABB centre to its edge along unit vector (ux, uy).
export function boxEdgeDistance(halfW, halfH, ux, uy) {
  const tx = Math.abs(ux) < 1e-6 ? Infinity : halfW / Math.abs(ux);
  const ty = Math.abs(uy) < 1e-6 ? Infinity : halfH / Math.abs(uy);
  return Math.min(tx, ty);
}

function cardBox(x, y, w, h) {
  return { x, y, w, h };
}

function seatChrome(seatH, inSeatBet) {
  if (inSeatBet) {
    if (seatH <= 46) return { vPad: 2, hPad: 4, micro: true };
    if (seatH <= 53) return { vPad: 4, hPad: 8, micro: false };
    return { vPad: 5, hPad: 9, micro: false };
  }
  if (seatH <= 35) return { vPad: 3, hPad: 4, micro: true };
  if (seatH <= 43) return { vPad: 5, hPad: 8, micro: false };
  return { vPad: 6, hPad: 9, micro: false };
}

function tagSize(seatH, inSeatBet) {
  return seatChrome(seatH, inSeatBet).micro
    ? { w: 16, h: 11 }
    : { w: TAG_W, h: TAG_H };
}

function tagBox(sx, sy, seatW, seatH, inSeatBet = false) {
  // Bottom-right of the card, on the reserved Bet $ row.
  const { w, h } = tagSize(seatH, inSeatBet);
  const right = sx + seatW / 2 - TAG_INSET;
  const bottom = sy + seatH / 2 - TAG_INSET;
  return {
    x: right - w / 2,
    y: bottom - h / 2,
    w,
    h,
  };
}

// Top name line inside the card. The tag lives on the stack line, so
// this box must stay clear of the pill.
export function nameLineBox(sx, sy, seatW, seatH, opts = {}) {
  const { vPad, hPad } = seatChrome(seatH, opts.inSeatBet);
  const nameH = 13;
  const w = Math.max(16, seatW - hPad * 2);
  const left = sx - seatW / 2 + hPad;
  return {
    x: left + w / 2,
    y: sy - seatH / 2 + vPad + nameH / 2,
    w,
    h: nameH,
  };
}

// Reserved "Bet $20" line under the stack. The D/SB/BB tag sits on
// this same bottom row, right-aligned. A bet appearing cannot resize
// the card. The stack line above it is full-width.
export const BET_PILL_H = 11;
export const MIN_STACK_TEXT_W = 36;

export function betLineBox(sx, sy, seatW, seatH) {
  const { vPad, hPad, micro } = seatChrome(seatH, true);
  const tagW = micro ? 16 : TAG_W;
  const w = Math.max(16, seatW - hPad * 2 - tagW);
  const left = sx - seatW / 2 + hPad;
  const bottom = sy + seatH / 2 - vPad;
  return {
    x: left + w / 2,
    y: bottom - BET_PILL_H / 2,
    w,
    h: BET_PILL_H,
  };
}

export function betPillBox(sx, sy, seatW, seatH) {
  return betLineBox(sx, sy, seatW, seatH);
}

// Stack amount line, full-width under the name. The tag lives on the
// reserved bet row, so it does not steal stack width.
export function stackLineBox(sx, sy, seatW, seatH, opts = {}) {
  const { vPad, hPad } = seatChrome(seatH, opts.inSeatBet);
  const stackH = 12;
  const w = Math.max(0, seatW - hPad * 2);
  const left = sx - seatW / 2 + hPad;
  const top = sy - seatH / 2 + vPad + 13 + 2;
  return {
    x: left + w / 2,
    y: top + stackH / 2,
    w,
    h: stackH,
  };
}

// Centre stack pieces that chips must never cover — empty pre-flop slots
// and a five-card river use the same card boxes. Matches TableLayout.jsx:
// cards → POT label → amount → reserved side-pot line → to-call.
export function reservedCentreBoxes(scale) {
  const s = scale || 1;
  const cardsH = 27 * s;
  const cardsMb = 13 * s;
  const labelH = 14 * s;
  const labelMb = 5 * s;
  const amountH = 37 * s;
  const sideMt = 6 * s;
  const sideH = 14 * s;
  const callMt = 7 * s;
  const callH = 16 * s;
  const stackH = cardsH + cardsMb + labelH + labelMb + amountH + sideMt + sideH + callMt + callH;
  let top = -stackH / 2;

  const box = (y, w, h, id) => ({ x: 0, y, w, h, id });
  const mid = (h) => top + h / 2;

  const cards = box(mid(cardsH), 5 * 20 * s + 4 * 6 * s + 4, cardsH + 4, 'cards');
  top += cardsH + cardsMb;
  const potLabel = box(mid(labelH), 36 * s + 8, labelH + 4, 'pot-label');
  top += labelH + labelMb;
  const potAmount = box(mid(amountH), 80 * s + 10, amountH + 6, 'pot-amount');
  top += amountH + sideMt;
  // Side-pot line is reserved in layout height (so the board does not
  // jump) but hidden with one pot — skip it as a chip obstacle.
  top += sideH + callMt;
  const toCall = box(mid(callH) + 4 * s, 120 * s + 8, callH + 14, 'to-call');
  return [cards, potLabel, potAmount, toCall];
}

export function reservedCentreBox(scale) {
  const boxes = reservedCentreBoxes(scale);
  let minX = Infinity;
  let maxX = -Infinity;
  let minY = Infinity;
  let maxY = -Infinity;
  boxes.forEach((b) => {
    minX = Math.min(minX, b.x - b.w / 2);
    maxX = Math.max(maxX, b.x + b.w / 2);
    minY = Math.min(minY, b.y - b.h / 2);
    maxY = Math.max(maxY, b.y + b.h / 2);
  });
  return {
    x: (minX + maxX) / 2,
    y: (minY + maxY) / 2,
    w: maxX - minX,
    h: maxY - minY,
  };
}

// Pot dollar amount only. Prefer reservedCentreBoxes for collision.
export function potTextBox(scale) {
  return reservedCentreBoxes(scale).find((b) => b.id === 'pot-amount');
}

function asBoxes(value) {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((b) => b && b.w && b.h);
  if (value.w && value.h) return [value];
  return [];
}

export function ovalContains(x, y, tableW, tableH, inset = 0) {
  const rx = tableW / 2 - inset;
  const ry = tableH / 2 - inset;
  if (rx <= 0 || ry <= 0) return false;
  return (x * x) / (rx * rx) + (y * y) / (ry * ry) <= 1 + 1e-6;
}

export function chipInsideOval(chip, tableW, tableH) {
  if (!chip) return false;
  // Seats straddle the rail. A hugging chip at the bottom seat's side
  // sits just outside the ellipse; ownership keeps it off the corners.
  return Math.abs(chip.x) <= tableW / 2 + 16
      && Math.abs(chip.y) <= tableH / 2 + 20;
}

export function chipInsideWrap(chip, wrapW, wrapH) {
  if (!chip) return false;
  if (!wrapW || !wrapH) return true;
  return Math.abs(chip.x) + chip.w / 2 <= wrapW / 2 - 1
      && Math.abs(chip.y) + chip.h / 2 <= wrapH / 2 - 1;
}

function isClear(chip, obstacles, gap) {
  for (let i = 0; i < obstacles.length; i += 1) {
    if (aabbOverlap(chip, obstacles[i], gap)) return false;
  }
  return true;
}

export function chipSeatDistance(chip, seat) {
  return Math.hypot(chip.x - seat.x, chip.y - seat.y);
}

export function nearestSeatIndex(chip, seats) {
  let best = -1;
  let bestD = Infinity;
  for (let i = 0; i < seats.length; i += 1) {
    const d = chipSeatDistance(chip, seats[i]);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  return best;
}

export function chipOwnedBySeat(chip, seats, ownerIndex) {
  if (!chip || ownerIndex < 0 || ownerIndex >= seats.length) return false;
  return nearestSeatIndex(chip, seats) === ownerIndex;
}

// Which inner edge of the card faces the pot. Vertical seats (top / bottom)
// put the chip on the horizontal inner edge. Side seats put it on the
// vertical inner edge — between that seat and the pot, not in a shared
// above/below gap with the neighbour. Never behind the seat.
export function chipSlotKind(angleDeg) {
  const norm = ((angleDeg % 360) + 360) % 360;
  const uy = Math.sin((norm * Math.PI) / 180);
  // Always the pot-facing horizontal edge. Inner left/right walks a
  // 44px pill a whole seat-width toward the board, which covers cards
  // and breaks nearest-seat ownership on 9–10 max.
  return uy >= -1e-9 ? 'above' : 'below';
}

function kindsFor(angleDeg) {
  const primary = chipSlotKind(angleDeg);
  const norm = ((angleDeg % 360) + 360) % 360;
  const ux = Math.cos((norm * Math.PI) / 180);
  // Outer vertical edge — away from x=0 — when the pot-facing edge is
  // blocked by the reserved centre. Inner left/right walks onto the
  // board and toward a neighbour.
  const outerSide = ux >= 0 ? 'right' : 'left';
  if (Math.abs(ux) < 0.25) {
    return [primary, 'left', 'right'];
  }
  return [primary, outerSide];
}

// Hug the seat. `along` is only large enough to sit left of the hanging
// D/SB tag on the top edge (above). Large along values walked chips
// onto the board.
function railSlotList(kind) {
  if (kind === 'above') {
    // Stay on the seat's top edge, left of the hanging tag. Extra along
    // is a last resort — it walks toward the to-call line.
    return [
      { along: 0, across: -22 },
      { along: 0, across: -23 },
      { along: 0, across: -21 },
      { along: 0, across: -24 },
      { along: 0, across: -20 },
      { along: 0, across: -28 },
      { along: 0, across: -18 },
      { along: 4, across: -22 },
    ];
  }
  return [
    { along: 0, across: 0 },
    { along: 0, across: 12 },
    { along: 0, across: -12 },
    { along: 0, across: 20 },
    { along: 0, across: -20 },
    { along: 0, across: 26 },
    { along: 0, across: -26 },
    { along: 2, across: 12 },
    { along: 4, across: 0 },
  ];
}

function acrossFlip(kind, seat) {
  if (kind === 'above') return 1;
  if (kind === 'left' || kind === 'right') {
    // Slide along the inner edge, away from the pot first.
    return seat.y >= 0 ? 1 : -1;
  }
  // Below: toward the near rail so a short-table chip does not
  // walk into the pot to escape its neighbour.
  return seat.x >= 0 ? 1 : -1;
}

function chipOnInnerEdge(kind, sx, sy, seatW, seatH, chipW, chipH, along, across) {
  const inset = CHIP_INSET + along;
  if (kind === 'above') {
    return { x: sx + across, y: sy - (seatH / 2 + inset + chipH / 2), w: chipW, h: chipH };
  }
  if (kind === 'below') {
    return { x: sx + across, y: sy + (seatH / 2 + inset + chipH / 2), w: chipW, h: chipH };
  }
  if (kind === 'right') {
    return { x: sx + (seatW / 2 + inset + chipW / 2), y: sy + across, w: chipW, h: chipH };
  }
  return { x: sx - (seatW / 2 + inset + chipW / 2), y: sy + across, w: chipW, h: chipH };
}

function hugCandidates(seat, seatW, seatH, chipW, chipH) {
  const kinds = kindsFor(seat.angle);
  const alongs = [0, 2, 4, 6];
  const acrosses = [0, 8, -8, 12, -12, 16, -16, 18, -18, 20, -20, 21, -21, 22, -22, 23, -23, 24, -24, 28, -28, 32, -32, 36, -36];
  const out = [];
  const seen = new Set();
  const push = (kind, along, across) => {
    const key = `${kind}:${along}:${across}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({
      ...chipOnInnerEdge(kind, seat.x, seat.y, seatW, seatH, chipW, chipH, along, across),
      kind,
      along,
      across,
    });
  };
  kinds.forEach((kind) => {
    const flip = acrossFlip(kind, seat);
    railSlotList(kind).forEach((slot) => push(kind, slot.along, slot.across * flip));
    alongs.forEach((along) => acrosses.forEach((across) => push(kind, along, across)));
  });
  return out;
}

function railSlotsFor(seat, seats, ownerIndex, seatW, seatH, chipW, chipH, fixed, tableW, tableH, wrapW, wrapH) {
  const slots = hugCandidates(seat, seatW, seatH, chipW, chipH).filter((cand) => (
    chipInsideOval(cand, tableW, tableH)
    && chipInsideWrap(cand, wrapW, wrapH)
    && isClear(cand, fixed, CHIP_GAP)
    && chipOwnedBySeat(cand, seats, ownerIndex)
  ));
  slots.sort((a, b) => chipSeatDistance(a, seat) - chipSeatDistance(b, seat));
  return slots;
}

// Place every live bet on a fixed inside-rail slot: the pot-facing edge of
// its own seat, hugging the card. Used on 2–4 player tables only (5–10
// use an in-seat ink pill). The board, POT label, pot amount and
// to-call line are a reserved zone. Each chip stays closer to its own
// seat than to any other. Tight seats are placed first. Seats, pot,
// pill, and buttons stay still.
export function placeBetChips({
  playerCount,
  heroIndex = 0,
  tableW,
  tableH,
  seatW,
  seatH,
  betting,
  scale = 1,
  chipW,
  chipH,
  wrapW = 0,
  wrapH = 0,
  pot = null,
  reserved = null,
  tags: liveTags = null,
}) {
  const n = Math.max(0, playerCount);
  const sized = chipSizeFor(n);
  chipW = chipW || sized.w;
  chipH = chipH || sized.h;
  const flags = betting || Array.from({ length: n }, () => true);
  const angles = seatAngles(n, heroIndex);
  const seats = angles.map((angle) => {
    const { x, y } = seatPosition(angle, tableW, tableH);
    return { angle, ...seatBox(x, y, seatW, seatH) };
  });
  const tags = liveTags
    ? asBoxes(liveTags)
    : seats.map((s) => tagBox(s.x, s.y, seatW, seatH));
  const extra = [...asBoxes(reserved), ...asBoxes(pot)];
  const reservedBoxes = extra.length ? extra : reservedCentreBoxes(scale);
  const fixed = [...reservedBoxes, ...seats, ...tags];

  const pending = [];
  for (let i = 0; i < seats.length; i += 1) {
    if (!flags[i]) continue;
    pending.push({
      i,
      slots: railSlotsFor(
        seats[i], seats, i, seatW, seatH, chipW, chipH,
        fixed, tableW, tableH, wrapW, wrapH,
      ),
    });
  }
  pending.sort((a, b) => a.slots.length - b.slots.length || a.i - b.i);

  const chips = seats.map(() => null);
  const fallbackChip = (i) => {
    const cands = hugCandidates(seats[i], seatW, seatH, chipW, chipH);
    const pick = cands.find((c) => (
      chipInsideOval(c, tableW, tableH)
      && chipInsideWrap(c, wrapW, wrapH)
      && chipOwnedBySeat(c, seats, i)
      && isClear(c, reservedBoxes, 0)
      && isClear(c, seats, 0)
      && isClear(c, tags, CHIP_GAP)
    ));
    if (pick) return pick;
    const kind = chipSlotKind(seats[i].angle);
    const across = kind === 'above' ? -22 : 0;
    return { ...chipOnInnerEdge(kind, seats[i].x, seats[i].y, seatW, seatH, chipW, chipH, 0, across), kind, along: 0, across };
  };

  const assign = (k) => {
    if (k >= pending.length) return true;
    const { i, slots } = pending[k];
    const others = chips.filter(Boolean);
    const options = slots.length ? slots : [fallbackChip(i)];
    for (let s = 0; s < options.length; s += 1) {
      if (slots.length && !isClear(options[s], others, CHIP_PEER_GAP)) continue;
      chips[i] = { ...options[s], i };
      if (assign(k + 1)) return true;
      chips[i] = null;
    }
    return false;
  };

  if (!assign(0)) {
    for (let p = 0; p < pending.length; p += 1) {
      const { i, slots } = pending[p];
      const placed = chips.filter(Boolean);
      const pick = slots.find((cand) => isClear(cand, placed, CHIP_PEER_GAP))
        || slots[0]
        || fallbackChip(i);
      chips[i] = { ...pick, i };
    }
  }

  const conflictsOf = (idx, chip) => {
    let count = 0;
    for (let j = 0; j < chips.length; j += 1) {
      if (j === idx || !chips[j]) continue;
      if (aabbOverlap(chip, chips[j], CHIP_PEER_GAP)) count += 1;
    }
    return count;
  };

  for (let iter = 0; iter < 24; iter += 1) {
    let best = null;
    for (let p = 0; p < pending.length; p += 1) {
      const { i, slots } = pending[p];
      if (!chips[i]) continue;
      const current = conflictsOf(i, chips[i]);
      if (current === 0) continue;
      for (let s = 0; s < slots.length; s += 1) {
        const next = conflictsOf(i, slots[s]);
        if (next >= current) continue;
        if (!best || next < best.next) best = { i, slot: slots[s], next };
      }
    }
    if (!best) break;
    chips[best.i] = { ...best.slot, i: best.i };
  }

  const nudgeKinds = ['above', 'below', 'left', 'right'];
  const nudgeAlong = [0, 4, 6, 8];
  const nudgeAcross = [0, 8, -8, 14, -14, 20, -20, 24, -24, 28, -28, 32, -32];
  for (let iter = 0; iter < 8; iter += 1) {
    let moved = false;
    for (let a = 0; a < chips.length; a += 1) {
      if (!chips[a]) continue;
      for (let b = a + 1; b < chips.length; b += 1) {
        if (!chips[b] || !aabbOverlap(chips[a], chips[b], 0)) continue;
        const tryNudge = (idx) => {
          const seat = seats[idx];
          const others = chips.filter((c, j) => c && j !== idx);
          const preferred = chips[idx].kind || chipSlotKind(seat.angle);
          const kinds = [preferred, ...nudgeKinds.filter((k) => k !== preferred)];
          for (let k = 0; k < kinds.length; k += 1) {
            for (let al = 0; al < nudgeAlong.length; al += 1) {
              for (let ac = 0; ac < nudgeAcross.length; ac += 1) {
                const cand = chipOnInnerEdge(
                  kinds[k], seat.x, seat.y, seatW, seatH, chipW, chipH,
                  nudgeAlong[al], nudgeAcross[ac],
                );
                if (!chipInsideOval(cand, tableW, tableH)) continue;
                if (!chipInsideWrap(cand, wrapW, wrapH)) continue;
                if (!isClear(cand, fixed, CHIP_GAP)) continue;
                if (!isClear(cand, others, CHIP_PEER_GAP)) continue;
                if (!chipOwnedBySeat(cand, seats, idx)) continue;
                chips[idx] = { ...cand, kind: kinds[k], along: nudgeAlong[al], across: nudgeAcross[ac], i: idx };
                return true;
              }
            }
          }
          return false;
        };
        if (tryNudge(b) || tryNudge(a)) moved = true;
      }
    }
    if (!moved) break;
  }

  return chips;
}

export function anyBetChipsOverlap(chips, gap = 0) {
  const live = chips.filter(Boolean);
  for (let i = 0; i < live.length; i += 1) {
    for (let j = i + 1; j < live.length; j += 1) {
      if (aabbOverlap(live[i], live[j], gap)) return true;
    }
  }
  return false;
}

export function anyChipHitsSeatsOrTags(chips, seats, tags, gap = 0) {
  for (const chip of chips) {
    if (!chip) continue;
    if (seats.some((s) => aabbOverlap(chip, s, gap))) return true;
    if (tags && tags.some((t) => aabbOverlap(chip, t, gap))) return true;
  }
  return false;
}

export function seatBoxesFor(playerCount, heroIndex, tableW, tableH, seatW, seatH) {
  const inSeat = usesInSeatBet(playerCount);
  return seatAngles(playerCount, heroIndex).map((angle) => {
    const { x, y } = seatPosition(angle, tableW, tableH);
    return {
      ...cardBox(x, y, seatW, seatH),
      tag: tagBox(x, y, seatW, seatH, inSeat),
      pill: inSeat ? betPillBox(x, y, seatW, seatH) : null,
    };
  });
}

// Seat card footprint by player count. This is the STARTING size — a full
// 10-handed table on a short phone has less oval perimeter than ten cards need,
// so computeTableMetrics shrinks from here until the seats actually fit.
//
// The heights are MEASURED from the rendered card, not estimated. Every
// seat is three lines (name, stack, reserved Bet $ + tag). Modelling a
// card shorter than it renders is not a rounding error — the fitting
// loop below reads these numbers, so a card that is 9px taller than the
// model says gets declared a fit while it is really overlapping its neighbour.
export const SEAT_H = {
  base: 47,
  dense: 43,
  micro: 35,
  // Reserved third row for the plain Bet $ line + D/SB/BB tag.
  inSeatBase: 57,
  inSeatDense: 52,
  inSeatMicro: 44,
};

// Phone bounds — the live game on a handset. computeTableMetrics() with no
// preset uses these, so the phone board's numbers do not move. Phone seats
// are the three-line (name / stack / Bet $) heights.
export const PHONE_TABLE = {
  minTableW: MIN_TABLE_W,
  maxTableW: MAX_TABLE_W,
  minTableH: MIN_TABLE_H,
  absMinTableH: ABS_MIN_TABLE_H,
  maxTableH: MAX_TABLE_H,
  maxAspect: MAX_ASPECT,
  minSeatW: MIN_SEAT_W,
  microSeatW: MICRO_SEAT_W,
  minScale: MIN_SCALE,
  maxScale: 1,
  denseFrom: DENSE_FROM,
  narrowW: NARROW_W,
  seatW: { denseNarrow: 88, dense: 96, baseNarrow: 108, base: 116 },
  seatH: { base: SEAT_H.inSeatBase, dense: SEAT_H.inSeatDense, micro: SEAT_H.inSeatMicro },
  scaleFrom: { tableH: 470, tableW: 268 },
  tv: false,
};

// TV / iPad / laptop table screen. Same geometry (angles, card-anchored bets,
// overlap loop) with a larger oval and larger seats so the board reads across
// a room. Phone callers never pass this preset.
export const TV_TABLE = {
  minTableW: 320,
  maxTableW: 880,
  minTableH: 300,
  absMinTableH: 240,
  maxTableH: 760,
  maxAspect: MAX_ASPECT,
  minSeatW: 110,
  microSeatW: 140,
  minScale: 1,
  maxScale: 2,
  denseFrom: DENSE_FROM,
  narrowW: 700,
  seatW: { denseNarrow: 132, dense: 148, baseNarrow: 152, base: 176 },
  seatH: { base: 62, dense: 56, micro: 46 },
  scaleFrom: { tableH: 360, tableW: 400 },
  tv: true,
};

export function tableBounds(preset) {
  if (preset === 'tv') return TV_TABLE;
  if (preset && typeof preset === 'object') return { ...PHONE_TABLE, ...preset };
  return PHONE_TABLE;
}

export function seatSize(wrapWidth, playerCount, preset, wrapHeight = 0) {
  const bounds = tableBounds(preset);
  const dense = playerCount >= bounds.denseFrom;
  const narrow = wrapWidth < bounds.narrowW;
  let height = dense ? bounds.seatH.dense : bounds.seatH.base;
  // 7–10 on a short phone wrap: start at the micro height so the oval is
  // taller and the width-shrink loop can leave more room for "$50.00"
  // plus the reserved Bet $ line.
  if (!bounds.tv && dense && wrapHeight > 0 && wrapHeight < 280) {
    height = bounds.seatH.micro;
  }
  return {
    dense,
    micro: false,
    tv: !!bounds.tv,
    width: dense
      ? (narrow ? bounds.seatW.denseNarrow : bounds.seatW.dense)
      : (narrow ? bounds.seatW.baseNarrow : bounds.seatW.base),
    height,
  };
}

// Breathing room required between two seat cards. Two cards that merely fail to
// overlap still read as one blob at seat size, and it absorbs the pixel or two
// of rounding between this model and the rendered box.
export const SEAT_GAP = 4;

// Do any two seats' cards come within SEAT_GAP of each other at this size?
// Axis-aligned boxes only overlap when they overlap on BOTH axes, which is why
// a tall oval tolerates seat spacing that a near-circular one does not.
export function anySeatsOverlap(playerCount, tableW, tableH, seatW, seatH) {
  if (playerCount < 2) return false;
  const points = seatAngles(playerCount, 0).map((a) => seatPosition(a, tableW, tableH));
  for (let i = 0; i < points.length; i += 1) {
    for (let j = i + 1; j < points.length; j += 1) {
      const dx = Math.abs(points[i].x - points[j].x);
      const dy = Math.abs(points[i].y - points[j].y);
      if (dx < seatW + SEAT_GAP && dy < seatH + SEAT_GAP) return true;
    }
  }
  return false;
}

// Side seats straddle the rail, so the oval may be as wide as the box minus one
// seat (plus a little, since the seat overlaps the rail rather than sitting
// outside it). Top and bottom seats do the same vertically.
function ovalFor(wrapWidth, wrapHeight, seatW, seatH, bounds = PHONE_TABLE) {
  let tableW = clamp(bounds.minTableW, Math.round(wrapWidth - seatW + 12), bounds.maxTableW);

  // Height the box can actually hold: the wrap, less a seat straddling the top
  // and bottom of the rail, less a little breathing room.
  const fits = Math.round(wrapHeight - seatH - 18);

  // A short viewport with a full table cannot hold MIN_TABLE_H *plus* a seat —
  // a 640pt phone at nine handed is the case. Clamping to MIN_TABLE_H there
  // pushes the bottom seat past the edge of the table area, and the bottom seat
  // is the VIEWER'S OWN: you would have to scroll to see your own stack. So
  // between MIN_TABLE_H and the hard floor the oval gives up height instead.
  const floor = wrapHeight > 0
    ? clamp(bounds.absMinTableH, fits, bounds.minTableH)
    : bounds.minTableH;

  let tableH = clamp(floor, fits, bounds.maxTableH);
  tableH = Math.min(tableH, Math.round(tableW * bounds.maxAspect));
  // Landscape TVs have leftover width. Pull the oval in so it stays a table
  // (near-circle) rather than a stadium. Phone callers never hit this.
  if (bounds.tv && tableH > 0) {
    tableW = Math.min(tableW, Math.round(tableH * 1.15));
    tableW = Math.max(tableW, bounds.minTableW);
  }
  return { tableW, tableH };
}

// The core sizing pass. `wrapWidth` / `wrapHeight` are the measured content box
// of the table area — i.e. the viewport minus the header and the action dock.
//
// Seats shrink until they fit around the oval rather than being a fixed size:
// ten seats on a 640pt phone have to be smaller than six on the same phone,
// because the oval they sit on is smaller too. Shrinking a seat also widens the
// oval (the oval is the box minus a seat), so the loop converges quickly.
export function computeTableMetrics(wrapWidth, wrapHeight, playerCount, preset) {
  const bounds = tableBounds(preset);
  const base = seatSize(wrapWidth, playerCount, bounds, wrapHeight);
  let seatW = base.width;
  let seatH = base.height;
  let oval = ovalFor(wrapWidth, wrapHeight, seatW, seatH, bounds);

  while (seatW > bounds.minSeatW && anySeatsOverlap(playerCount, oval.tableW, oval.tableH, seatW, seatH)) {
    seatW -= 2;
    // Past this width the card also drops to its tightest type scale, which
    // buys height as well — and height is the other half of an overlap.
    seatH = seatW < bounds.microSeatW ? bounds.seatH.micro : base.height;
    oval = ovalFor(wrapWidth, wrapHeight, seatW, seatH, bounds);
  }

  const minH = bounds.seatH.micro;
  if (!bounds.tv) {
    while (wrapHeight > 0 && oval.tableH + seatH > wrapHeight && seatH > minH) {
      seatH -= 1;
      oval = ovalFor(wrapWidth, wrapHeight, seatW, seatH, bounds);
    }
  }

  const seat = { ...base, width: seatW, height: seatH, micro: seatW < bounds.microSeatW || seatH <= minH };
  const { tableW, tableH } = oval;

  // One scale factor drives the whole centre readout (board cards, pot, side
  // pots, to-call) so the pot shrinks before it can ever crowd a seat.
  const scale = clamp(
    bounds.minScale,
    Math.min(tableH / bounds.scaleFrom.tableH, tableW / bounds.scaleFrom.tableW),
    bounds.maxScale,
  );

  // If the dock is so tall that even the minimum oval doesn't fit (a very short
  // viewport, e.g. landscape), say so and let the caller allow scrolling rather
  // than silently clipping seats off the top and bottom.
  const overflowing = wrapHeight > 0 && tableH + seat.height > wrapHeight;

  return { tableW, tableH, scale, seat, overflowing };
}

// ---------------------------------------------------------------------------
// Dock reservations.
//
// The oval is sized from whatever the dock leaves behind, so ANY change in dock
// height resizes the table. Left alone, the circle would grow and shrink as the
// hand moves — Fold/Call/Raise appears on your turn and vanishes on everyone
// else's, the first action of a round adds the history line — which reads as
// the table breathing rather than as a stable object you're sitting at.
//
// So the dock reserves its variable rows at a constant height instead: the
// table is always sized as if the action buttons were on screen, whether they
// are or not, and the oval never moves during a hand.

// History moved into the header Menu. The dock no longer reserves a log line.
export const LOG_ROW_H = 0;

// The action slot. Kept at 100px even after New Hand / Rebuys / Settle Up
// moved into Host tools, so the oval does not jump. The tallest live content
// is now Fold/Call/Raise (60) or Declare Winner (56); shorter content is
// centred in the slot, which doubles as breathing room under the banner.
//
// Fold/Call/Raise now carry a reserved 37px Check/Fold pill row above them
// (+8px gap), held on your turn too, so the live content is 105.
export const ACTION_SLOT_H = 105;

// The status banner. One line for most of a hand ("Flop • Waiting on Jony to
// act"), two at showdown ("Showdown • Compare hands, then declare the winner"),
// so it is reserved at its two-line height (58px pill + its 12px margin) and
// the pill is centred in it. Left alone this was a 20px table resize at the end
// of every hand — the last thing still moving once the action slot was fixed.
//
// The banner is now locked to one 36px line (it never wraps), so the slot is
// that plus its 12px top margin.
export const BANNER_SLOT_H = 48;

// ---------------------------------------------------------------------------
// Bet-chip boxes. Used by tests (and the TV page) so "never overlap" is the
// same model as the rendered chip: a pill hanging off the pot-facing edge
// of the seat card, never a radius.
// ---------------------------------------------------------------------------

export const PHONE_CHIP = { width: 52, height: 22, gap: 9 };
export const TV_CHIP = { width: 58, height: 26, gap: 10 };

export function chipBox(angleDeg, tableW, tableH, seat, chip = PHONE_CHIP) {
  const { x, y } = seatPosition(angleDeg, tableW, tableH);
  const cy = betAnchor(angleDeg) === 'below'
    ? y + seat.height / 2 + chip.gap + chip.height / 2
    : y - seat.height / 2 - chip.gap - chip.height / 2;
  return { x, y: cy, w: chip.width, h: chip.height };
}

export function boxesOverlap(a, b, pad = 0) {
  return Math.abs(a.x - b.x) * 2 < a.w + b.w + pad && Math.abs(a.y - b.y) * 2 < a.h + b.h + pad;
}

export function seatBox(angleDeg, tableW, tableH, seat) {
  const { x, y } = seatPosition(angleDeg, tableW, tableH);
  return { x, y, w: seat.width, h: seat.height };
}
